"use client";

import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useLogout, useWallet, useModal } from "@getpara/react-sdk";
import { useTokenStore, useWalletStore } from "@/lib/zustand";
import { ChainId, ChainKey, ChainType } from "@lifi/sdk";
import { zeroAddress } from "viem";
import { cn } from "@/lib/utils";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { useDisconnect } from "wagmi";

export function WalletNetworkSwitcher() {
  const { fromChain, setFromChain, setFromToken } = useTokenStore();
  const { isEmailOrSocialWallet, setIsEmailOrSocialWallet } = useWalletStore();
  const { data: wallet } = useWallet();
  const { logoutAsync } = useLogout();
  const { openModal } = useModal();
  const { disconnect: disconnectEvm } = useDisconnect();
  const { publicKey: solanaPublicKey, disconnect: disconnectSolana } =
    useSolanaWallet();

  // Detect email/social wallet and update shared store (only goes true, never false)
  useEffect(() => {
    const detected = !!((wallet as any)?.email || (wallet as any)?.userId || (wallet as any)?.externalId);
    if (detected) {
      setIsEmailOrSocialWallet(true);
    }
  }, [wallet, setIsEmailOrSocialWallet]);

  const handleSwitch = async (type: "EVM" | "SVM") => {
    try {
      if (fromChain.chainType === ChainType.EVM && type === "EVM") return;
      if (fromChain.chainType === ChainType.SVM && type === "SVM") return;

      if (type === "SVM") {
        setFromChain({
          id: ChainId.SOL,
          name: "Solana",
          logoURI: "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/solana.svg",
          chainType: ChainType.SVM,
          symbol: ChainKey.SOL,
        });
        setFromToken({
          chainId: ChainId.SOL,
          address: "11111111111111111111111111111111",
          symbol: "SOL",
          name: "Solana",
          decimals: 9,
          logoURI: "https://assets.coingecko.com/coins/images/4128/small/solana.png?1640133422",
          priceUSD: "0",
        });
        if (!isEmailOrSocialWallet) {
          try { disconnectEvm(); } catch { /* ignore */ }
          await logoutAsync().catch(() => {});
          await new Promise(resolve => setTimeout(resolve, 300));
          openModal({ step: 'AUTH_MAIN' });
        }
        // Email wallets: only update chain/token state, let use-auto-switch-chain
        // handle wallet type switch via setCurrentWalletIds
      } else {
        if (solanaPublicKey) {
          try { await disconnectSolana(); } catch { /* ignore */ }
        }
        setFromChain({
          id: 1,
          name: "Ethereum",
          logoURI: "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/ethereum.svg",
          chainType: ChainType.EVM,
          symbol: ChainKey.ETH,
        });
        setFromToken({
          chainId: 1,
          address: zeroAddress,
          symbol: "ETH",
          name: "Ethereum",
          decimals: 18,
          logoURI: "https://tokens.1inch.io/0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee.png",
          priceUSD: "0",
        });
        if (!isEmailOrSocialWallet) {
          await logoutAsync().catch(() => {});
          await new Promise(resolve => setTimeout(resolve, 300));
          openModal({ step: 'AUTH_MAIN' });
        }
      }
    } catch (error) {
      console.error("Error switching wallet network:", error);
    }
  };

  const isSolana = fromChain.chainType === ChainType.SVM;

  return (
    <div className="flex bg-muted/20 p-1 rounded-xl border border-border/50 min-w-0">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleSwitch("EVM")}
        className={cn(
          "rounded-lg px-2 sm:px-3 py-1 h-8 text-[11px] sm:text-xs font-medium transition-all",
          !isSolana 
            ? "bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary" 
            : "text-muted-foreground hover:text-foreground"
        )}
      >
        EVM
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleSwitch("SVM")}
        className={cn(
          "rounded-lg px-2 sm:px-3 py-1 h-8 text-[11px] sm:text-xs font-medium transition-all",
          isSolana 
            ? "bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary" 
            : "text-muted-foreground hover:text-foreground"
        )}
      >
        Solana
      </Button>
    </div>
  );
}
